import {
  ArrowCounterClockwiseIcon,
  ArrowLeftIcon,
  ChartLineUp,
  CursorClick,
  DownloadSimple,
  Eye,
  Globe,
  Megaphone,
  MoneyIcon,
  TrendUp,
  WarningCircle,
  Briefcase,
  ShieldCheck,
  Target,
  Clock,
  CurrencyDollar,
  ArrowSquareOut,
  IdentificationBadge,
} from "@phosphor-icons/react";
import dayjs from "dayjs";
import { Button } from "flowbite-react";
import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AnimatedModal from "../../../../components/AnimatedModal";
import Drawer from "../../../../components/Drawer";
import InlineEdit from "../../../../components/InlineEdit";
import StatusSelect from "../../../../components/StatusSelect";
import CampaignDetailSkeleton from "../../../../components/SkeletonLoaders/CampaignDetailSkeleton";
import { Table } from "../../../../components/Table";
import {
  CurrencyCode,
} from "../../../../shared/constants";
import { getCurrencySymbol, getAmountByCurrency } from "../../../../lib/utils";
import { useDateRange } from "../../../../contexts/DateRangeContext";
import { useMetaAdsCampaignDetail } from "../../../../hooks/get/useMetaAdsCampaignDetail";
import { useMetaAdsUpdateCampaign } from "../../../../hooks/post/useMetaAdsUpdateCampaign";
import { IDateRangePreset } from "../../../../interfaces";
import { cn, formatNumber } from "../../../../lib/utils";
import { META_CAMPAIGN_STATUS } from "../../../../shared/constants";
import PerformanceChart from "../../components/PerformanceChart";
import BaseCampaignDetail from "../../components/shared/BaseCampaignDetail";
import { IMetaAdSet } from "../../interface/metaAds";

const MetaAdsCampaignDetail: React.FC = () => {
  const { campaign_id } = useParams<{ campaign_id: string }>();
  const [searchParams] = useSearchParams();
  const trackingId = searchParams.get("trackingId");
  const navigate = useNavigate();
  const { dateRange, setDateRange, selectedPreset, setSelectedPreset } = useDateRange();
  const { t } = useTranslation();

  // Modal states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<META_CAMPAIGN_STATUS | null>(null);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [pendingBudget, setPendingBudget] = useState<string>("");
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [selectedAdSet, setSelectedAdSet] = useState<IMetaAdSet | null>(null);
  const [showAdSetDetailDrawer, setShowAdSetDetailDrawer] = useState(false);

  const updateCampaignMutation = useMetaAdsUpdateCampaign();

  const handleUpdateBudget = async (newValue: string) => {
    setPendingBudget(newValue);
    setShowBudgetModal(true);
  };

  const handleConfirmBudgetChange = async () => {
    setIsActionLoading(true);
    try {
      await updateCampaignMutation.mutateAsync({
        campaignId: campaign_id || "",
        data: { daily_budget: parseFloat(pendingBudget) * 1000000 },
      });
      toast.success(
        t(
          "campaigns.manageCampaign.budgetUpdatedSuccessfully",
          "Budget updated successfully!",
        ),
      );
      setShowBudgetModal(false);
      refetch();
    } catch (error) {
      toast.error(
        t(
          "campaigns.manageCampaign.errors.failedToUpdateBudget",
          "Failed to update budget",
        ),
      );
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleStatusSwitch = (value: META_CAMPAIGN_STATUS) => {
    setPendingStatus(value);
    setShowStatusModal(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!pendingStatus) return;
    setIsActionLoading(true);
    try {
      await updateCampaignMutation.mutateAsync({
        campaignId: campaign_id || "",
        data: { status: pendingStatus as any },
      });
      toast.success(
        t(
          "campaigns.manageCampaign.statusUpdatedSuccessfully",
          "Status updated successfully!",
        ),
      );
      setShowStatusModal(false);
      refetch();
    } catch (error) {
      toast.error(
        t(
          "campaigns.manageCampaign.errors.failedToUpdateStatus",
          "Failed to update status",
        ),
      );
    } finally {
      setIsActionLoading(false);
    }
  };

  const { data, isLoading, isError, refetch } = useMetaAdsCampaignDetail({
    campaign_id: campaign_id || "",
    startDate: dateRange.startDate.toISOString().split("T")[0],
    endDate: dateRange.endDate.toISOString().split("T")[0],
  });

  const campaignDetail = data;
  const campaign = campaignDetail?.campaign;
  const currency = (campaign?.default_currency as CurrencyCode) || "USD";
  const metrics = campaignDetail?.metrics;
  const dailyMetrics = campaignDetail?.daily_metrics || [];
  const adsets = campaignDetail?.adsets || [];

  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    setDateRange(startDate, endDate);
  };

  const handlePresetChange = (preset: IDateRangePreset | undefined) => {
    setSelectedPreset(preset);
  };

  if (isLoading) return <CampaignDetailSkeleton />;

  const hasFailed = isError || !data;

  if (hasFailed) {
    return (
      <div className="flex flex-col gap-3 h-full">
        {/* Error State Center */}
        <div className="flex-1 flex flex-col items-center justify-center p-8 bg-white rounded-lg shadow-sm border border-gray-100 min-h-[400px]">
          <div className="p-4 bg-red-50 rounded-full mb-4">
            <WarningCircle
              size={36}
              className="text-red-500"
              weight="duotone"
            />
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">
            {t(
              "campaigns.errors.oopsSomethingWentWrong",
              "Oops! Something went wrong",
            )}
          </h2>
          <p className="text-sm text-gray-500 mb-6 max-w-sm text-center">
            {t(
              "campaigns.errors.failedToLoadDetails",
              "We couldn't load the details for this campaign. The data might be unavailable, or there is an issue with the connection.",
            )}
          </p>
          <div className="flex items-center gap-3">
            <Button color="light" onClick={() => navigate(-1)}>
              <div className="flex items-center gap-2">
                <ArrowLeftIcon size={20} weight="duotone" />
                {t("common.goBack", "Go Back")}
              </div>
            </Button>
            <Button color="default" onClick={() => refetch()}>
              <div className="flex items-center gap-2">
                <ArrowCounterClockwiseIcon size={20} weight="duotone" />
                {t("common.tryAgain", "Try Again")}
              </div>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <BaseCampaignDetail
      headerProps={{
        title: campaign?.campaign_name || t("campaigns.detail.title", "Campaign Details"),
        platform: "meta_ads",
        type: "detail",
        appInfo: {
          id: campaign?.meta_campaign_id || "",
          name: campaign?.app_name || campaign?.campaign_name || "",
          logoUrl: campaign?.app_logo || null,
        },
        pillSwitch: (
          <StatusSelect
            value={
              (campaign?.meta_campaign_status || "PAUSED") === "ACTIVE"
                ? META_CAMPAIGN_STATUS.ACTIVE
                : META_CAMPAIGN_STATUS.PAUSED
            }
            options={[
              {
                label: t("common.manageCampaign.paused", "Paused"),
                value: META_CAMPAIGN_STATUS.PAUSED,
                color: "red",
              },
              {
                label: t("common.manageCampaign.active", "Active"),
                value: META_CAMPAIGN_STATUS.ACTIVE,
                color: "green",
              },
            ]}
            onChange={handleStatusSwitch}
          />
        ),
        onHistoryClick: () => navigate(`/campaigns/meta-ads/${campaign_id}/history${trackingId ? `?trackingId=${trackingId}` : ""}`),
        onDateRangeChange: handleDateRangeChange,
        onPresetChange: handlePresetChange,
        dateRange: dateRange,
        selectedPresetId: selectedPreset?.id,
      }}
      statsCounts={7}
      stats={metrics ? [
        {
          title: t("campaigns.metrics.totalSpend", "Total Spend"),
          value: getAmountByCurrency(metrics as Record<string, any> || {}, "total_spend", currency),
          isCurrency: true,
          icon: <MoneyIcon size={20} className="text-gray-700" weight="duotone" />,
        },
        {
          title: t("campaigns.metrics.impressions", "Impressions"),
          value: metrics?.total_impressions || 0,
          icon: <Eye size={20} className="text-gray-700" weight="duotone" />,
        },
        {
          title: t("campaigns.metrics.clicks", "Clicks"),
          value: metrics?.total_clicks || 0,
          icon: <CursorClick size={20} className="text-gray-700" weight="duotone" />,
        },
        {
          title: t("campaigns.metrics.installs", "Installs"),
          value: metrics?.total_installs || 0,
          icon: <DownloadSimple size={20} className="text-gray-700" weight="duotone" />,
        },
        {
          title: t("campaigns.metrics.ctr", "CTR"),
          value: (metrics?.average_ctr || 0),
          isPercentage: true,
          icon: <TrendUp size={20} className="text-gray-700" weight="duotone" />,
        },
        {
          title: "Reach",
          value: metrics?.total_reach || 0,
          icon: <Globe size={20} className="text-gray-700" weight="duotone" />,
        },
        {
          title: "CPC",
          value: getAmountByCurrency(metrics as Record<string, any> || {}, "average_cpc", currency),
          isCurrency: true,
          icon: <MoneyIcon size={20} className="text-gray-700" weight="duotone" />,
        },
      ] : undefined}
      currency={currency as CurrencyCode}
      chart={dailyMetrics && dailyMetrics.length > 0 ? (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold flex items-center gap-2 text-gray-800">
              <ChartLineUp size={20} className="text-gray-700" weight="duotone" />
              {t("campaigns.detail.performanceOverTime", "Performance Over Time")}
            </h3>
          </div>
          <div className="h-[350px]">
            <PerformanceChart
              height={350}
              currency={currency}
              currencySymbol={getCurrencySymbol(currency as CurrencyCode)}
              data={dailyMetrics.map((m) => ({
                ...m,
                spend_usd: getAmountByCurrency(m as unknown as Record<string, any>, "spend", currency),
              }))}
            />
          </div>
        </div>
      ) : undefined}
      sidebar={campaign ? (
        <div className="flex flex-col gap-6">
          <h3 className="text-sm font-bold flex items-center gap-2 text-gray-800">
            <TrendUp size={20} className="text-gray-700" weight="duotone" />
            {t("campaigns.detail.campaignSettings", "Campaign Settings")}
          </h3>
          <div className="flex flex-col gap-6 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
            <PremiumInfoRow
              label={t("campaigns.detail.dailyBudget", "Daily Budget")}
              value={
                <InlineEdit
                  value={Number((() => {
                    const raw = (campaign as any)?.[`daily_budget_${currency.toLowerCase()}`];
                    return typeof raw === 'object' && raw !== null ? raw.parsedValue : (raw || 0);
                  })())}
                  onSave={handleUpdateBudget}
                  prefix={getCurrencySymbol(currency as CurrencyCode)}
                  type="number"
                  editIconPlacement="start"
                  alwaysShowEdit
                />
              }
              isPrimary
            />
            <PremiumInfoRow
              label={t("campaigns.detail.objective", "Objective")}
              value={(campaign?.meta_campaign_objective || "").toLowerCase().replace(/_/g, " ")}
            />
            <PremiumInfoRow
              label={t("campaigns.detail.buyingType", "Buying Type")}
              value={(campaign?.meta_buying_type || "").toLowerCase().replace(/_/g, " ")}
            />
            <PremiumInfoRow
              label={t("campaigns.detail.bidStrategy", "Bid Strategy")}
              value={(campaign?.meta_bid_strategy || "").toLowerCase().replace(/_/g, " ") || "-"}
            />
            <PremiumInfoRow
              label={t("campaigns.detail.startTime", "Start Date")}
              value={campaign?.meta_campaign_start_time ? dayjs(campaign.meta_campaign_start_time).format("DD MMM YYYY") : "-"}
            />
          </div>
        </div>
      ) : undefined}
    >
      {/* Confirmation Modals */}
      <AnimatedModal
        show={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title={t("common.manageCampaign.confirmStatusChange", "Confirm Status Change")}
        isConfirmation={true}
        onConfirm={handleConfirmStatusChange}
        confirmText={t("common.confirm", "Confirm")}
        cancelText={t("common.cancel", "Cancel")}
        isConfirming={isActionLoading}
        confirmButtonColor={pendingStatus === META_CAMPAIGN_STATUS.ACTIVE ? "green" : "red"}
      >
        <div className="flex flex-col items-center gap-4 py-4">
          <div className={`p-3 rounded-full ${pendingStatus === META_CAMPAIGN_STATUS.ACTIVE ? "bg-green-100" : "bg-red-100"}`}>
            <Megaphone size={32} className={pendingStatus === META_CAMPAIGN_STATUS.ACTIVE ? "text-green-600" : "text-red-600"} weight="duotone" />
          </div>
          <p className="text-center text-gray-600">
            {t(
              "common.manageCampaign.statusChangeConfirmation",
              `Are you sure you want to ${pendingStatus === META_CAMPAIGN_STATUS.ACTIVE ? "enable" : "pause"} this campaign?`,
            )}
          </p>
        </div>
      </AnimatedModal>

      <AnimatedModal
        show={showBudgetModal}
        onClose={() => setShowBudgetModal(false)}
        title={t("common.manageCampaign.confirmBudgetChange", "Confirm Budget Change")}
        isConfirmation={true}
        onConfirm={handleConfirmBudgetChange}
        confirmText={t("common.confirm", "Confirm")}
        cancelText={t("common.cancel", "Cancel")}
        isConfirming={isActionLoading}
        confirmButtonColor="indigo"
      >
        <div className="flex flex-col items-center gap-4 py-4">
          <div className="p-3 rounded-full bg-indigo-100">
            <MoneyIcon size={32} className="text-indigo-600" weight="duotone" />
          </div>
          <div className="text-center">
            <p className="text-gray-600 mb-2">
              {t("common.manageCampaign.budgetChangeConfirmation", "Are you sure you want to update the daily budget?")}
            </p>
            <div className="flex items-center justify-center gap-3 font-bold text-lg">
              <span className="text-gray-400 line-through">
                {getCurrencySymbol(currency as CurrencyCode)}
                {(() => {
                  const raw = (campaign as any)?.[`daily_budget_${currency.toLowerCase()}`];
                  const budget = typeof raw === 'object' && raw !== null ? raw.parsedValue : raw;
                  return (budget || 0).toLocaleString();
                })()}
              </span>
              <span className="text-gray-300">→</span>
              <span className="text-indigo-600">
                {getCurrencySymbol(currency as CurrencyCode)}{formatNumber(parseFloat(pendingBudget) || 0)}
              </span>
            </div>
          </div>
        </div>
      </AnimatedModal>

      {/* Ad Sets Section */}
      {adsets && adsets.length > 0 && (
        <div className="mt-2 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold flex items-center gap-2 text-gray-800">
              <Briefcase size={20} className="text-gray-700" weight="duotone" />
              {t("campaigns.detail.adSets", "Ad Sets")}
              <span className="ml-1 px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-[10px] font-bold">
                {adsets.length}
              </span>
            </h3>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <Table<IMetaAdSet>
              data={adsets}
              columns={[
                {
                  accessorKey: "meta_adset_name",
                  header: t("campaigns.columns.adSetName", "Ad Set Name"),
                  size: 300,
                  cell: ({ row }) => (
                    <div className="flex flex-col gap-1 py-1">
                      <span className="text-sm font-medium text-gray-700" title={row.original.meta_adset_name}>
                        {row.original.meta_adset_name}
                      </span>
                      <span className="text-[10px] text-gray-400 font-mono">
                        ID: {row.original.meta_adset_id}
                      </span>
                    </div>
                  ),
                },
                {
                  accessorKey: "meta_adset_status",
                  header: "Status",
                  size: 100,
                  cell: ({ row }) => {
                    const status = row.original.meta_adset_status;
                    const isActive = status === "ACTIVE";
                    return (
                      <div className={cn(
                        "inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider",
                        isActive ? "bg-green-50 text-green-700 border border-green-100" : "bg-gray-50 text-gray-700 border border-gray-100"
                      )}>
                        <div className={cn("w-1.5 h-1.5 rounded-full", isActive ? "bg-green-500" : "bg-gray-500")} />
                        {status}
                      </div>
                    );
                  },
                },
                {
                  accessorKey: "meta_bid_amount",
                  header: "Bid / Cap",
                  size: 120,
                  cell: ({ row }) => {
                    const bidUsd = row.original.bid_amount_usd?.parsedValue || 0;
                    return (
                      <div className="text-sm font-medium text-gray-700">
                        {getCurrencySymbol(currency as CurrencyCode)}{bidUsd.toLocaleString()}
                      </div>
                    );
                  },
                },
                {
                  id: "actions",
                  header: "Actions",
                  size: 80,
                  cell: ({ row }) => (
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => {
                        setSelectedAdSet(row.original);
                        setShowAdSetDetailDrawer(true);
                      }}
                    >
                      <div className="flex items-center gap-1">
                        <Eye size={14} weight="duotone" />
                        {t("common.view", "View")}
                      </div>
                    </Button>
                  ),
                },
              ]}
              isLoading={false}
              loadingHeight={2.0}
              loadingRowCount={3}
            />
          </div>
        </div>
      )}

      <Drawer
        show={showAdSetDetailDrawer}
        onClose={() => setShowAdSetDetailDrawer(false)}
        title={t("campaigns.detail.adSetDetails", "Ad Set Details")}
        direction="right"
        className="flex flex-col overflow-hidden w-[500px]"
      >
        {selectedAdSet && (
          <div className="flex flex-col h-full">
            {/* Sticky Sub-Header in Drawer */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 pb-4 -mx-4 -mt-4 shadow-sm">
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  <IdentificationBadge size={22} className="text-indigo-600" weight="duotone" />
                  <h4 className="text-lg font-bold text-gray-900 truncate max-w-[300px]" title={selectedAdSet.meta_adset_name}>
                    {selectedAdSet.meta_adset_name}
                  </h4>
                </div>
                <div className="flex items-center gap-3 text-[10px] font-mono text-gray-400">
                  <span className="bg-gray-100 px-2 py-0.5 rounded text-gray-500">
                    ID: {selectedAdSet.meta_adset_id}
                  </span>
                  <div className={cn(
                    "flex items-center gap-1 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider",
                    selectedAdSet.meta_adset_status === "ACTIVE"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                      : "bg-gray-100 text-gray-600 border border-gray-200"
                  )}>
                    <div className={cn("w-1 h-1 rounded-full", selectedAdSet.meta_adset_status === "ACTIVE" ? "bg-emerald-500" : "bg-gray-400")} />
                    {selectedAdSet.meta_adset_status}
                  </div>
                </div>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto py-8 flex flex-col gap-2 custom-scrollbar">
              <div className="grid grid-cols-1 gap-4">
                {/* Delivery Settings Card */}
                <div className="flex flex-col gap-5 p-5 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-2 border-b border-gray-50 pb-3">
                    <div className="p-1.5 bg-indigo-50 rounded-lg">
                      <CurrencyDollar size={18} className="text-indigo-600" weight="duotone" />
                    </div>
                    <h5 className="text-sm font-bold text-gray-800 uppercase tracking-tight">
                      Delivery & Budget
                    </h5>
                  </div>
                  <div className="flex flex-col gap-4">
                    <PremiumInfoRow
                      label="Daily Budget"
                      value={(() => {
                        const budgetObj = selectedAdSet.meta_daily_budget;
                        const budget = typeof budgetObj === 'object' && budgetObj !== null ? budgetObj.parsedValue : budgetObj;
                        return budget ? `${getCurrencySymbol(currency as CurrencyCode)}${budget.toLocaleString()}` : "-";
                      })()}
                      isPrimary
                    />
                    <PremiumInfoRow
                      label="Bid Amount"
                      value={(() => {
                        const bidObj = selectedAdSet.meta_bid_amount;
                        const bid = typeof bidObj === 'object' && bidObj !== null ? bidObj.parsedValue : bidObj;
                        const bidUsd = selectedAdSet.bid_amount_usd?.parsedValue;
                        const finalBid = bid ?? bidUsd;
                        return finalBid ? `${getCurrencySymbol(currency as CurrencyCode)}${finalBid.toLocaleString()}` : "-";
                      })()}
                    />
                    <PremiumInfoRow label="Optimization" value={selectedAdSet.meta_optimization_goal?.toLowerCase().replace(/_/g, " ")} />
                    <PremiumInfoRow label="Billing Event" value={selectedAdSet.meta_billing_event?.toLowerCase().replace(/_/g, " ")} />
                    <PremiumInfoRow label="Bid Strategy" value={selectedAdSet.meta_bid_strategy?.toLowerCase().replace(/_/g, " ") || "-"} />
                  </div>
                </div>

                {/* Targeting Details Card */}
                <div className="flex flex-col gap-5 p-5 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-2 border-b border-gray-50 pb-3">
                    <div className="p-1.5 bg-rose-50 rounded-lg">
                      <Target size={18} className="text-rose-600" weight="duotone" />
                    </div>
                    <h5 className="text-sm font-bold text-gray-800 uppercase tracking-tight">
                      Audience Targeting
                    </h5>
                  </div>
                  <div className="flex flex-col gap-4">
                    <PremiumInfoRow
                      label="Locations"
                      value={
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-right">{selectedAdSet.meta_targeting?.geo_locations?.countries?.join(", ") || selectedAdSet.meta_targeting?.geo_locations?.country_groups?.join(", ") || "All"}</span>
                          {selectedAdSet.meta_targeting?.excluded_geo_locations?.countries && (
                            <span className="text-[10px] text-rose-500 font-bold bg-rose-50 px-1.5 py-0.5 rounded leading-none">
                              Excl: {selectedAdSet.meta_targeting.excluded_geo_locations.countries.join(", ")}
                            </span>
                          )}
                        </div>
                      }
                    />
                    <PremiumInfoRow
                      label="Age Range"
                      value={selectedAdSet.meta_targeting?.age_range ? `${selectedAdSet.meta_targeting.age_range[0]} - ${selectedAdSet.meta_targeting.age_range[1]}` : "-"}
                    />
                    <PremiumInfoRow
                      label="Genders"
                      value={selectedAdSet.meta_targeting?.genders?.map((g: number) => g === 1 ? "Male" : "Female").join(", ") || "All"}
                    />
                    <PremiumInfoRow
                      label="Platform/OS"
                      value={
                        <div className="flex gap-1.5 flex-wrap justify-end">
                          {(selectedAdSet.meta_targeting?.user_os || ["All Platforms"]).map((os, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] font-bold uppercase">{os}</span>
                          ))}
                        </div>
                      }
                    />
                    <PremiumInfoRow
                      label="Devices"
                      value={selectedAdSet.meta_targeting?.user_device?.join(", ") || "All Devices"}
                    />
                  </div>
                  {selectedAdSet.meta_targeting?.targeting_automation?.advantage_audience === 1 && (
                    <div className="flex items-center gap-2 mt-auto pt-3 border-t border-gray-50 bg-emerald-50/30 -mx-5 -mb-5 px-5 py-3 rounded-b-2xl">
                      <ShieldCheck size={18} className="text-emerald-500" weight="duotone" />
                      <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider">Advantage Audience Active</span>
                    </div>
                  )}
                </div>

                {/* Attribution Spec Card */}
                {selectedAdSet.meta_attribution_spec && selectedAdSet.meta_attribution_spec.length > 0 && (
                  <div className="flex flex-col gap-4 p-5 bg-white rounded-2xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-2 border-b border-gray-50 pb-3">
                      <div className="p-1.5 bg-amber-50 rounded-lg">
                        <Clock size={18} className="text-amber-600" weight="duotone" />
                      </div>
                      <h5 className="text-sm font-bold text-gray-800 uppercase tracking-tight">
                        Attribution Specification
                      </h5>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                      {selectedAdSet.meta_attribution_spec.map((spec, idx) => (
                        <div key={idx} className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex flex-col gap-1.5 group hover:bg-white hover:border-amber-200 transition-all duration-300">
                          <span className="text-[10px] text-gray-400 uppercase font-black tracking-widest">{spec.event_type.replace(/_/g, " ")}</span>
                          <span className="text-base font-bold text-gray-800 group-hover:text-amber-700">{spec.window_days} Day Window</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Brand Safety Card */}
                {selectedAdSet.meta_targeting?.brand_safety_content_filter_levels && (
                  <div className="flex flex-col gap-4 p-5 bg-white rounded-2xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-2 border-b border-gray-50 pb-3">
                      <div className="p-1.5 bg-blue-50 rounded-lg">
                        <ShieldCheck size={18} className="text-blue-600" weight="duotone" />
                      </div>
                      <h5 className="text-sm font-bold text-gray-800 uppercase tracking-tight">
                        Brand Safety
                      </h5>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedAdSet.meta_targeting.brand_safety_content_filter_levels.map((level, idx) => (
                        <span key={idx} className="text-[11px] bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg font-bold border border-blue-100 shadow-sm">
                          {level.replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Promoted Object Card */}
                {selectedAdSet.meta_promoted_object && (
                  <div className="flex flex-col gap-4 p-5 bg-white rounded-2xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-2 border-b border-gray-50 pb-3">
                      <div className="p-1.5 bg-gray-50 rounded-lg">
                        <ArrowSquareOut size={18} className="text-gray-600" weight="duotone" />
                      </div>
                      <h5 className="text-sm font-bold text-gray-800 uppercase tracking-tight">
                        Promoted Object
                      </h5>
                    </div>
                    <a
                      href={selectedAdSet.meta_promoted_object.object_store_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3.5 bg-gray-50 rounded-xl border border-gray-100 hover:bg-indigo-50 hover:border-indigo-200 transition-all duration-300 group"
                    >
                      <span className="text-[11px] font-mono text-gray-500 break-all w-[90%] group-hover:text-indigo-600">
                        {selectedAdSet.meta_promoted_object.object_store_url}
                      </span>
                      <ArrowSquareOut size={16} className="text-gray-400 group-hover:text-indigo-600" />
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </BaseCampaignDetail>
  );
};

const PremiumInfoRow = ({ label, value, isPrimary = false, capitalize = true }: { label: string; value: React.ReactNode; isPrimary?: boolean; capitalize?: boolean }) => (
  <div className="flex items-baseline justify-between gap-4 group">
    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider flex-shrink-0">
      {label}
    </span>
    <div className={cn(
      "text-sm font-semibold truncate",
      isPrimary ? "text-indigo-600 text-base" : "text-gray-700",
      capitalize && typeof value === 'string' ? "capitalize" : ""
    )}>
      {value}
    </div>
  </div>
);

export default MetaAdsCampaignDetail;
